import defaultMetrics from '@/organisms/plans/utils/defaultMetrics';
import ActionType from '@/shared/enums/ActionType';
import EntityType from '@/shared/enums/EntityType';
import entityLabel from '@/shared/utils/entityLabel';
import defaultMetricGroup from '@pkg/entities/snapshots/utils/defaultMetricGroup';
import percent from '@pkg/utils/numbers/percent';
import Config from '@/lib/config';

export default function updatedHours({
  actionMap,
  actionType = ActionType.UPDATE_HOURS,
  activity,
  comparisonScenario,
  scenario,
}) {
  const originalActivity = comparisonScenario.relationships
    .get(EntityType.ACTIVITY)
    ?.get(activity?.uuid);
  const role =
    activity.owner_type === EntityType.ROLE
      ? scenario.relationships.get(EntityType.ROLE)?.get(activity.owner_uuid)
      : null;
  const originalRole =
    originalActivity.owner_type === EntityType.ROLE
      ? comparisonScenario.relationships
          .get(EntityType.ROLE)
          ?.get(originalActivity.owner_uuid)
      : null;
  const group = role
    ? scenario?.relationships.get(EntityType.GROUP)?.get(role.group_uuid)
    : scenario?.relationships.get(EntityType.GROUP)?.get(activity.owner_uuid);

  const hoursDifference = activity.hours - originalActivity.hours;
  const hoursFactor = percent(hoursDifference, originalActivity.hours, 8) / 100;

  const activityBudget = activity.__metrics?.self.visible.budget || 0;
  const originalActivityBudget =
    originalActivity.__metrics?.self.visible.budget || 0;
  const allocatedFTE = activity.__metrics?.self.visible.allocatedFTE || 0;
  const originalAllocatedFTE =
    originalActivity.__metrics?.self.visible.allocatedFTE || 0;

  const activityCost = activity.__metrics?.self.visible.cost || 0;
  const originalActivityCost =
    originalActivity.__metrics?.self.visible.cost || 0;

  const metrics = {
    activityBudget,
    hours: activity.hours,
    budget: activityBudget,
    cost: activityCost,
    allocatedFTE,
  };

  const existingAction = actionMap?.get(actionType);

  const action = existingAction ?? {
    entity: EntityType.ACTIVITY,
    impact: { ...defaultMetricGroup(), activityBudget: 0 },
    metrics: defaultMetrics({ comparisonScenario }),
    type: actionType,
    [EntityType.ACTIVITY]: new Map(),
    [EntityType.ROLE]: new Set(),
    [EntityType.GROUP]: new Set(),
  };

  if (role) {
    action[EntityType.ROLE].add(role.uuid);
  }
  if (group) {
    action[EntityType.GROUP].add(group.uuid);
  }

  const hoursChange = metrics.hours - originalActivity.hours;
  action.metrics[1].hours += hoursChange;

  const budgetChange = metrics.budget - originalActivityBudget;
  action.metrics[1].budget += budgetChange;

  const activityBudgetChange = metrics.activityBudget - originalActivityBudget;
  action.metrics[1].activityBudget += activityBudgetChange;

  const allocatedFTEChange = metrics.allocatedFTE - originalAllocatedFTE;
  action.metrics[1].allocatedFTE += allocatedFTEChange;

  const costChange = metrics.cost - originalActivityCost;
  action.metrics[1].cost += costChange;

  // Impact changes.
  const factor = role ? hoursChange / Config.FTE_HOURS_PER_WEEK : 0;

  const fteImpact = role ? role.fte * factor : 0;

  action.impact.activities += 1;
  action.impact.hours += hoursChange;
  action.impact.roles = action[EntityType.ROLE].size;
  action.impact.budget += budgetChange;
  action.impact.activityBudget += activityBudgetChange;
  action.impact.cost += costChange;
  action.impact.allocatedFTE += allocatedFTEChange;
  action.impact.fte += fteImpact;

  // Aggregate the individual activity action metrics.
  const actionActivity = action[EntityType.ACTIVITY].get(activity.uuid) ?? {
    impact: defaultMetricGroup(),
    metrics: defaultMetrics({ action, activity, comparisonScenario }),
  };
  actionActivity.metrics[1].hours += hoursChange;
  actionActivity.metrics[1].budget += budgetChange;
  actionActivity.metrics[1].activityBudget += activityBudgetChange;
  actionActivity.metrics[1].allocatedFTE += allocatedFTEChange;
  actionActivity.metrics[1].cost += costChange;
  action[EntityType.ACTIVITY].set(activity.uuid, actionActivity);
  actionActivity.impact.activities = 1;
  actionActivity.impact.hours = hoursChange;
  actionActivity.impact.roles = 1;
  actionActivity.impact.budget = budgetChange;
  actionActivity.impact.activityBudget = activityBudgetChange;
  actionActivity.impact.cost = costChange;
  actionActivity.impact.allocatedFTE += allocatedFTEChange;
  actionActivity.impact.fte += fteImpact;

  const activityCount = action[EntityType.ACTIVITY].size;
  const roleCount = action[EntityType.ROLE].size;

  const roleLabel = entityLabel(EntityType.ROLE, roleCount !== 1);
  const activityLabel = entityLabel(EntityType.ACTIVITY, activityCount !== 1);
  const verb = roleCount === 1 ? 'in' : 'across';

  action.title = `Hours were updated for ${activityCount} ${activityLabel} ${verb} ${roleCount} ${roleLabel}`;

  return action;
}
